import React, { useState } from "react";
import {
  Spinner,
  Checkbox,
  TableBodyProps,
  Tbody as ChakraTBody,
  Td,
  Tr
} from "@chakra-ui/react";
import { get } from "lodash-es";
import { types } from "../../duck";
import { BodyContext } from "./duck";

interface TBodyProps extends TableBodyProps {
  data: any[];
  columns: types.ColumnProps[];
  onRowSelect?: (id: string | number) => void;
  selectedRows: (string | number)[];
  isTableEditable: boolean;
  loading?: boolean;
  noDataMessage?: string;
}

const TBody: React.FC<TBodyProps> = ({
  data,
  isTableEditable = false,
  columns,
  onRowSelect,
  selectedRows,
  loading,
  noDataMessage,
  ...rest
}) => {
  const [editable, setEditable] = useState(false);
  const [pending, setPending] = React.useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [currentCellIndex, setCurrentCellIndex] = useState<string | null>(null);

  const handleCellClick = (rowIndex: number, cellIndex: string) => {
    if (pending) {
      return;
    }
    setEditable(true);
    setCurrentRowIndex(rowIndex);
    setCurrentCellIndex(cellIndex);
  };

  return (
    <BodyContext.Provider value={{ setEditable, setPending }}>
      <ChakraTBody {...rest}>
        {data.length > 0 ? (
          data.map((record, index) => {
            const isRowSelected =
              onRowSelect && selectedRows.includes(record._id || record.fakeId);
            return (
              <Tr
                position="relative"
                key={index}
                borderBottom="1px solid #EDF2F7"
              >
                {onRowSelect && (
                  <Td w="14" pl="6" pr="3">
                    <Checkbox
                      colorScheme="dark"
                      borderColor="dark.500"
                      onChange={() => {
                        onRowSelect(record._id || record.fakeId);
                      }}
                      isChecked={isRowSelected}
                    />
                  </Td>
                )}
                {columns.map((column, i) => {
                  const value = get(record, column.dataKey || "");
                  const cellId = `${record._id || record.fakeId}_${
                    column.label
                  }`;
                  const isEditable =
                    editable &&
                    isTableEditable &&
                    index === currentRowIndex &&
                    cellId === currentCellIndex;

                  return (
                    <Td
                      key={cellId}
                      height="0"
                      px={isEditable && i !== 0 ? 0 : 6}
                      py={isEditable && i !== 0 ? 0 : 4}
                      onClick={() => {
                        handleCellClick(index, cellId);
                      }}
                      bgColor={
                        i === 0 ? record.mainColor || "white" : record.color
                      }
                      borderRight={
                        isTableEditable && !column.sticky
                          ? "1px solid #EDF2F7"
                          : "none"
                      }
                      borderLeft={
                        !column.sticky && i === 0 && isTableEditable
                          ? "1px solid #EDF2F7"
                          : "none"
                      }
                      boxShadow={
                        isTableEditable && column.sticky
                          ? "inset 1px 0 #EDF2F7, inset -1px 0 #EDF2F7"
                          : "none"
                      }
                      w={column.w}
                      position={column.sticky ? "sticky" : "static"}
                      left={column.sticky ? 0 : "auto"}
                    >
                      {column.render
                        ? column.render({
                            value,
                            record,
                            isEditable,
                            setEditable
                          })
                        : value}
                    </Td>
                  );
                })}
              </Tr>
            );
          })
        ) : loading ? (
          <Tr>
            <Td colSpan={columns.length} textAlign="center">
              <Spinner />
            </Td>
          </Tr>
        ) : (
          <Tr>
            {noDataMessage ? (
              <Td colSpan={columns.length} textAlign="center">
                {noDataMessage}
              </Td>
            ) : (
              columns.map((column, i) => (
                <Td
                  key={i}
                  position={column.sticky ? "sticky" : "static"}
                  left={column.sticky ? 0 : "auto"}
                  bgColor="white"
                >
                  -
                </Td>
              ))
            )}
          </Tr>
        )}
      </ChakraTBody>
    </BodyContext.Provider>
  );
};

export default TBody;
